import { useMemo } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { MultiSelect } from '@/components/ui/multi-select';
import { useAllocatedDedicatedClis } from '@/features/voice-campaigns/api';
import { useRunningDids } from '../../api';
import type { InboundComposeFormValues } from '../../types';

interface Props {
  values: InboundComposeFormValues;
  setValues: (patch: Partial<InboundComposeFormValues>) => void;
}

export function StepDids({ values, setValues }: Props) {
  const dedicatedClis = useAllocatedDedicatedClis();
  const runningDids = useRunningDids();

  /** Filter out DIDs already bound to another running inbound campaign. */
  const usedDids = useMemo(
    () =>
      new Set((runningDids.data ?? []).map((d) => String(d.did ?? d.cli ?? ''))),
    [runningDids.data],
  );
  const cliOptions = useMemo(
    () =>
      (dedicatedClis.data ?? [])
        .filter((c) => !usedDids.has(String(c.cli)))
        .map((c) => ({ value: String(c.cliId), label: c.cli })),
    [dedicatedClis.data, usedDids],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-1">
          DIDs (CLIs)<span className="text-destructive">*</span>
        </CardTitle>
        <CardDescription>
          Pick which dedicated CLIs answer this campaign. DIDs already used by
          another inbound campaign are hidden.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <MultiSelect
          options={cliOptions}
          value={values.selectedCLI}
          onChange={(v) => setValues({ selectedCLI: v })}
          placeholder={
            dedicatedClis.isLoading
              ? 'Loading…'
              : cliOptions.length === 0
                ? 'No dedicated CLIs available'
                : 'Pick CLIs'
          }
          searchPlaceholder="Filter CLIs…"
        />
      </CardContent>
    </Card>
  );
}
